import CloseIcon from '@base/components/src/Icon/CloseIcon';
import EditIcon from '@base/components/src/Icon/EditIcon';
import Radio from '@base/components/src/Radio';
import RadioGroupBase from '@base/components/src/RadioGroupBase';
import Spinner from '@base/components/src/Spinner';
import { ServiceInfoProduct } from '@package/core/src/shoppingCart/types';
import useServiceMissingItem from '@package/hooks/src/useServiceMissingItem';
import currencyFormatting from '@package/utils/src/currencyFormatting';
import config from 'config-base';
import { useCallback, useMemo, useState } from 'react';
import useLabelVatiants from '@package/hooks/src/useLabelVatiants';
import styles from './MissingItemModal.module.css';
import Button from '@base/components/src/Button';

const EMPTY_VALUE = '';

const serviceAcceptClass = `url(${config.mainPyramydairUrl}/i/serviceAccept.png)`;
const cancelServiceClass = `url(${config.mainPyramydairUrl}/i/cancelService.png)`;

const isPyrApp = config.brandCode === '0';

function Loader() {
  return (
    <div className={styles.loader}>
      <Spinner className="!right-auto" width="w-10" height="h-10" />
    </div>
  );
}

type ServiceRelatedProductsProps = {
  buyButtonAvailable: boolean;
  products: Array<ServiceInfoProduct>;
  submitService(relatedProductSku: string): Promise<void>;
  cancelService(): void;
};

function ServiceRelatedProducts(props: ServiceRelatedProductsProps) {
  const { buyButtonAvailable, products, submitService, cancelService } = props;

  const initialValue =
    [...products].sort((productA, productB) => productA.price - productB.price)[0].sku || EMPTY_VALUE;

  const [selectedProduct, setSelectedProduct] = useState<string>(initialValue);

  const options = useMemo(
    () =>
      products.map((product) => ({
        label: product.name,
        value: product.sku,
        optionProps: { image: product.image, price: product.price },
      })),
    [products],
  );

  const handleSubmitService = useCallback(async () => {
    const product = products.find((item) => item.sku === selectedProduct);
    if (product) {
      await submitService(product.sku);
    }
  }, [products, selectedProduct]);

  return (
    <div>
      <RadioGroupBase<{ image: string; price: number }>
        name="serviceProduct"
        value={selectedProduct}
        onChange={(e) => setSelectedProduct(e.target.value)}
        classNameRoot={styles.serviceProductsContainer}
        options={options}
        renderRadio={({ optionProps, ...rest }) =>
          optionProps ? (
            <Radio
              {...rest}
              size="small"
              className={styles.serviceRadioContainer}
              radioLabelClassName={styles.serviceRadioLabel}
              radioClassName={styles.serviceRadioButton}
              label={
                <div className={styles.serviceProduct}>
                  <img
                    className={styles.serviceProductImage}
                    src={optionProps.image}
                    width={148}
                    height={90}
                    alt="option"
                  />
                  <p className={styles.serviceProductName}>{rest.label}</p>
                  <p className="my-0 text-red">
                    <div className="font-bold my-0">{currencyFormatting(optionProps.price)}</div>
                  </p>
                </div>
              }
            />
          ) : null
        }
      />
      {buyButtonAvailable ? (
        <div className="flex mt-3 ml-4">
          {isPyrApp ? (
            <>
              <button
                data-testid={'service-related-submit-plan-btn'}
                className={styles.serviceAccept}
                style={{ backgroundImage: serviceAcceptClass }}
                onClick={handleSubmitService}
              />
              <button
                className={styles.cancelService}
                data-testid={'service-related-cancel-plan-btn'}
                style={{ backgroundImage: cancelServiceClass }}
                onClick={cancelService}
              />
            </>
          ) : (
            <>
              <Button
                label={<p className={styles.addServiceAgdBtnLabel}>Add Service</p>}
                classes={`${styles.agdServiceButton} mr-4`}
                onClick={handleSubmitService}
                data-testid={'service-related-add-service-btn'}
              />
              <Button
                classes={styles.agdServiceButton}
                onClick={cancelService}
                variant={'outlined'}
                data-testid={'service-related-cancel-service-btn'}
                label={<p className={styles.addServiceAgdBtnLabel}>Cancel</p>}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

type MissingItemsModalProps = {
  productSku: string;
  serviceId: number;
  addService(): Promise<void>;
  closeModal(): void;
  infoMode?: boolean;
};

export default function MissingItemsModal(props: MissingItemsModalProps) {
  const { productSku, serviceId, addService, closeModal, infoMode = false } = props;
  const { serviceInfo, addMissingItem, isFetching } = useServiceMissingItem({
    relatedProductSku: productSku,
    serviceId,
  });
  const { v } = useLabelVatiants();
  if (!serviceInfo || (!serviceInfo && !infoMode && isFetching)) return <Loader />;

  const price = serviceInfo.price ? ` | ${currencyFormatting(serviceInfo.price)}` : '';

  return !infoMode ? (
    <div className={`${styles.serviceModalContent} modalContent`}>
      <p className={styles.missingItemModalTitle}>
        {serviceInfo.name}
        {price}
        {serviceInfo.editContentUrl && <EditIcon url={serviceInfo.editContentUrl} />}
      </p>
      {serviceInfo.largeImage && <img className={styles.largeImage} src={serviceInfo.largeImage} alt="service" />}
      <div className={styles.serviceDescription} dangerouslySetInnerHTML={{ __html: serviceInfo.desc }} />
      {!serviceInfo.buyButtonAvailable && (
        <div className={styles.missingItemMessage}>
          <p className={styles.missingItemMessageText}>{v('missingCart')}</p>
          <div className={styles.clearIconContainer}>
            <CloseIcon data-testid={'service-related-close-icon'} className={styles.clearIcon} onClick={closeModal} />
          </div>
        </div>
      )}
      {serviceInfo.serviceAdditionalProducts.length > 0 && (
        <div className={`${styles.additionalProductContainer} ${styles.serviceProductsContainer}`}>
          {serviceInfo.serviceAdditionalProducts.map((product: any) => (
            <div key={product.sku} className={styles.serviceProduct}>
              <img
                className={styles.serviceProductImage}
                src={product.image}
                width={148}
                height={90}
                alt="service product"
              />
              <p className={styles.serviceProductName}>{product.name}</p>
            </div>
          ))}
        </div>
      )}
      <p className={styles.serviceNote}>Select one of these cases to add to your cart</p>
      <div className="relative">
        {serviceInfo.editServiceUrl && <EditIcon className={styles.editIcon} url={serviceInfo.editServiceUrl} />}
        <ServiceRelatedProducts
          buyButtonAvailable={serviceInfo.buyButtonAvailable}
          products={serviceInfo.serviceRelatedProducts}
          submitService={async (relatedProductSku) => {
            await addMissingItem({
              baseProductSku: productSku,
              serviceId,
              relatedProductSku,
            });
            closeModal();
          }}
          cancelService={closeModal}
        />
      </div>
      <div className={styles.serviceNote} dangerouslySetInnerHTML={{ __html: serviceInfo.content }} />
    </div>
  ) : (
    <div className={`${styles.serviceModalContent} modalContent`}>
      <p className={styles.missingItemModalTitle}>
        {serviceInfo.name}
        {price}
        {serviceInfo.editContentUrl && <EditIcon url={serviceInfo.editContentUrl} />}
      </p>
      {serviceInfo.largeImage && <img className={styles.largeImage} src={serviceInfo.largeImage} alt="servise info" />}
      <div className={styles.serviceDescription} dangerouslySetInnerHTML={{ __html: serviceInfo.desc }} />
      {serviceInfo.buyButtonAvailable ? (
        <div className="flex mt-3 ml-4">
          {isPyrApp ? (
            <>
              <button
                className={styles.serviceAccept}
                style={{ backgroundImage: serviceAcceptClass }}
                onClick={async () => {
                  await addService();
                  closeModal();
                }}
              />
              <button
                className={styles.cancelService}
                style={{ backgroundImage: cancelServiceClass }}
                onClick={closeModal}
              />
            </>
          ) : (
            <>
              <Button
                label={<p className={styles.addServiceAgdBtnLabel}>Add Service</p>}
                classes={`${styles.agdServiceButton} mr-4`}
                onClick={async () => {
                  await addService();
                  closeModal();
                }}
              />
              <Button
                classes={styles.agdServiceButton}
                onClick={closeModal}
                variant={'outlined'}
                label={<p className={styles.addServiceAgdBtnLabel}>Cancel</p>}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
